import { type HTMLAttributes } from 'react';

import { FlexAligner, Typography } from '@scaleaq/scaleui';

import { getSegmentedChipsLoaderBorderRadius } from '@/components/chips/chips.constants.ts';
import { PillContainer } from '@/components/chips/segmentedChips/segmentedChips.style.ts';
import { Loader } from '@/components/Loader.tsx';
import { colors, responsive } from '@/utils';

import { ChipsItem, ChipsSize, SegmentedChipsRadiusRules } from './chips.types.ts';
import { ChipsButton } from './ChipsButton.tsx';

type SegmentedChipsProps<T = undefined> = Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  disabled?: boolean;
  isLoading?: boolean;
  items: ChipsItem<T>[];
  label?: string;
  onChange: (item: ChipsItem<T>) => void;
  /** Border radius for the pill container and inner chips. Defaults to `pill`. */
  radiusRules?: SegmentedChipsRadiusRules;
  size?: ChipsSize;
  value: ChipsItem<T>;
};

export const SegmentedChips = <T = undefined,>({
  disabled = false,
  isLoading = false,
  items,
  label,
  onChange,
  radiusRules = 'pill',
  size = 'medium',
  value,
  ...rest
}: SegmentedChipsProps<T>) => (
  <FlexAligner
    direction='column'
    gap={responsive.spacing.xsmall}
    style={{ position: 'relative' }}
    fullWidth={false}
  >
    <Loader
      render={isLoading}
      size='medium'
      borderRadius={getSegmentedChipsLoaderBorderRadius(size, radiusRules)}
      backgroundColor={colors.background.transparent['0']}
    />
    {label && <Typography type='buttontext_medium'>{label}</Typography>}
    <PillContainer {...rest} $size={size} $radiusRules={radiusRules}>
      {items.map(item => (
        <ChipsButton
          {...item}
          key={item.id}
          disabled={disabled}
          radiusRules={radiusRules}
          size={size}
          variant='group'
          selected={value.id === item.id}
          onClick={e => {
            item.onClick?.(e);
            onChange(item);
          }}
        />
      ))}
    </PillContainer>
  </FlexAligner>
);
